import {Box2Like, Size2Like, Vector2Like, wrap} from "@cm/math"
import {MipLevelRegion, MipLevelRegions, Region, Regions} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/image-op-descriptor"
import {getMipLevelSize} from "@cm/hal"

export const mergeRegionsInplace = <T extends Region>(
    regions: T[],
    options?: {fuseFn?: (masterRegion: T, fusingRegion: T) => void; equalityThresh?: number},
) => {
    // merge regions (TODO: this might need some optimization when the number of regions is large)
    const equalityThresh = options?.equalityThresh ?? 0.0
    const isEqual = (a: number, b: number): boolean => Math.abs(a - b) <= equalityThresh
    while (true) {
        let somethingMerged = false
        for (let a = 0; a < regions.length; a++) {
            const regionA = regions[a]
            for (let b = a + 1; b < regions.length; b++) {
                const regionB = regions[b]
                // check if regions can be merged
                const canMerge =
                    (isEqual(regionA.x, regionB.x) &&
                        isEqual(regionA.width, regionB.width) &&
                        (isEqual(regionA.y, regionB.y + regionB.height) || isEqual(regionA.y + regionA.height, regionB.y))) ||
                    (isEqual(regionA.y, regionB.y) &&
                        isEqual(regionA.height, regionB.height) &&
                        (isEqual(regionA.x, regionB.x + regionB.width) || isEqual(regionA.x + regionA.width, regionB.x)))
                if (canMerge) {
                    regions.splice(b, 1)
                    const minX = Math.min(regionA.x, regionB.x)
                    const minY = Math.min(regionA.y, regionB.y)
                    regionA.width = Math.max(regionA.x + regionA.width, regionB.x + regionB.width) - minX
                    regionA.height = Math.max(regionA.y + regionA.height, regionB.y + regionB.height) - minY
                    regionA.x = minX
                    regionA.y = minY
                    options?.fuseFn?.(regionA, regionB)
                    b--
                    somethingMerged = true
                } else {
                    // check if one region is completely inside the other
                    if (
                        regionA.x >= regionB.x &&
                        regionA.y >= regionB.y &&
                        regionA.x + regionA.width <= regionB.x + regionB.width &&
                        regionA.y + regionA.height <= regionB.y + regionB.height
                    ) {
                        regions.splice(a, 1)
                        options?.fuseFn?.(regionB, regionA)
                        a--
                        somethingMerged = true
                        break // no need to check further for this region
                    } else if (
                        regionB.x >= regionA.x &&
                        regionB.y >= regionA.y &&
                        regionB.x + regionB.width <= regionA.x + regionA.width &&
                        regionB.y + regionB.height <= regionA.y + regionA.height
                    ) {
                        regions.splice(b, 1)
                        options?.fuseFn?.(regionA, regionB)
                        b--
                        somethingMerged = true
                    }
                }
            }
        }
        if (!somethingMerged) {
            break
        }
    }
}

export const checkValidMipLevelRegions = (mipLevelRegions: MipLevelRegions, maxMipLevel?: number) => {
    for (const mipLevelRegion of mipLevelRegions) {
        checkValidMipLevelRegion(mipLevelRegion, maxMipLevel)
    }
}

export const checkValidMipLevelRegion = (mipLevelRegion: MipLevelRegion, maxMipLevel?: number) => {
    if (mipLevelRegion.mipLevel < 0) {
        throw new Error("Mip level must be non-negative")
    }
    if (maxMipLevel !== undefined && mipLevelRegion.mipLevel > maxMipLevel) {
        throw new Error(`Mip level ${mipLevelRegion.mipLevel} exceeds maximum mip level ${maxMipLevel}`)
    }
    if (mipLevelRegion.width <= 0 || mipLevelRegion.height <= 0) {
        throw new Error("Mip level region must have positive width and height")
    }
}

export const applyWrapToMipLevelRegion = (fullImageSize: Size2Like, region: MipLevelRegion, options?: {useCeilSize?: boolean}): MipLevelRegions => {
    return applyWrapToRegion(fullImageSize, region, region.mipLevel, options).map((wrappedRegion) => ({
        ...wrappedRegion,
        mipLevel: region.mipLevel,
    }))
}

export const wrapOffset = (fullImageSize: Size2Like, offset: Vector2Like, mipLevel: number, options?: {useCeilSize?: boolean}): Vector2Like => {
    const imageSize = getMipLevelSize(fullImageSize, mipLevel, options)
    return {
        x: wrap(offset.x, imageSize.width),
        y: wrap(offset.y, imageSize.height),
    }
}

export const applyWrapToRegion = (fullImageSize: Size2Like, region: Region, mipLevel: number, options?: {useCeilSize?: boolean}): Regions => {
    const imageSize = getMipLevelSize(fullImageSize, mipLevel, options)
    const x = wrap(region.x, imageSize.width)
    const y = wrap(region.y, imageSize.height)
    const w = Math.min(region.width, imageSize.width)
    const h = Math.min(region.height, imageSize.height)
    let remainingAreas: Box2Like[]
    if (x + w > imageSize.width) {
        // we need to split the region vertically
        remainingAreas = [
            {
                x: x,
                y: y,
                width: imageSize.width - x,
                height: h,
            },
            {
                x: 0,
                y: y,
                width: w - (imageSize.width - x),
                height: h,
            },
        ]
    } else {
        remainingAreas = [
            {
                x: x,
                y: y,
                width: w,
                height: h,
            },
        ]
    }
    const resultingRegions: Regions = []
    for (const area of remainingAreas) {
        if (area.y + area.height > imageSize.height) {
            // we need to split the region horizontally
            resultingRegions.push({
                x: area.x,
                y: area.y,
                width: area.width,
                height: imageSize.height - area.y,
            })
            resultingRegions.push({
                x: area.x,
                y: 0,
                width: area.width,
                height: area.height - (imageSize.height - area.y),
            })
        } else {
            resultingRegions.push({
                x: area.x,
                y: area.y,
                width: area.width,
                height: area.height,
            })
        }
    }
    return resultingRegions
}

export const remapMipLevelRegion = (region: MipLevelRegion, targetMipLevel: number): MipLevelRegion => {
    return {
        ...mapRegionToMipLevel(region, region.mipLevel, targetMipLevel),
        mipLevel: targetMipLevel,
    }
}

export const mapOffsetToMipLevel = (offset: Vector2Like, sourceMipLevel: number, targetMipLevel: number): Vector2Like => {
    if (sourceMipLevel < 0 || targetMipLevel < 0) {
        throw new Error("Mip levels must be non-negative")
    }
    const scale = Math.pow(2, sourceMipLevel - targetMipLevel)
    return {
        x: Math.floor(offset.x * scale),
        y: Math.floor(offset.y * scale),
    }
}

export const mapRegionToMipLevel = (region: Region, sourceMipLevel: number, targetMipLevel: number): Region => {
    if (sourceMipLevel < 0 || targetMipLevel < 0) {
        throw new Error("Mip levels must be non-negative")
    }
    const scale = Math.pow(2, sourceMipLevel - targetMipLevel)
    const p00 = {
        x: Math.floor(region.x * scale),
        y: Math.floor(region.y * scale),
    }
    const p11 = {
        x: Math.ceil((region.x + region.width) * scale),
        y: Math.ceil((region.y + region.height) * scale),
    }
    return {
        x: p00.x,
        y: p00.y,
        width: p11.x - p00.x,
        height: p11.y - p00.y,
    }
}

export const dilateRegion = (region: Region, dilation: Vector2Like): Region => {
    if (dilation.x < 0 || dilation.y < 0) {
        throw new Error("Dilation must be non-negative")
    }
    return {
        x: region.x - dilation.x,
        y: region.y - dilation.y,
        width: region.width + 2 * dilation.x,
        height: region.height + 2 * dilation.y,
    }
}

export const dilateRegionUniform = (region: Region, dilation: number): Region => {
    if (dilation < 0) {
        throw new Error("Dilation must be non-negative")
    }
    return {
        x: region.x - dilation,
        y: region.y - dilation,
        width: region.width + 2 * dilation,
        height: region.height + 2 * dilation,
    }
}

export const offsetRegion = (region: Region, offset: Vector2Like): Region => {
    return {
        x: region.x + offset.x,
        y: region.y + offset.y,
        width: region.width,
        height: region.height,
    }
}

export const clipRegion = (region: Region, clip: Region): Region => {
    const x = Math.max(region.x, clip.x)
    const y = Math.max(region.y, clip.y)
    return {
        x: x,
        y: y,
        width: Math.min(region.x + region.width, clip.x + clip.width) - x,
        height: Math.min(region.y + region.height, clip.y + clip.height) - y,
    }
}

export const isRegionEmpty = (region: Region): boolean => {
    return region.width <= 0 || region.height <= 0
}

// Samplers wrap continuous coordinates at the exact size of a mip level (size / 2^mipLevel), while requested regions wrap at the ceil'd mip level size (see applyWrapToRegion).
// Shifting a requested range by whole exact periods into the first one keeps both within a texel of each other, which the margin covers (including the interpolation taps).
export const WRAP_REGION_MARGIN = 3

export const shiftRangeIntoFirstWrapPeriod = (min: number, max: number, period: number): {min: number; max: number} => {
    const shift = Math.floor(min / period) * period
    return {min: min - shift, max: max - shift}
}
